'use client';

import { formatDate } from 'dates';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, DataTableColumns, Typography } from 'ui';

import { getStatusTranslation } from '~/contract/utils';
import { Contract } from '~/data';

import { ContractStatus } from '../shared';

type RenderProps = {
  row: Contract;
};

const ActiveFromCell = ({ row }: RenderProps) => {
  return <ContractStatus status={row.status} startDate={row.startDate} createdDate={row.createdDate} />;
};

const CustomerName = ({ row }: RenderProps) => <Typography>{row.customerName}</Typography>;

type CellRenderMap = {
  [key: string]: (props: RenderProps) => React.ReactNode;
};

const CELL_RENDER_MAP: CellRenderMap = {
  customer: CustomerName,
};

export const ContractTable = ({ list }: { list: Contract[] }) => {
  const { t } = useTranslation('contracts');

  const tableColumns = useMemo(() => {
    const columns: DataTableColumns<Contract>[] = [
      { accessor: 'customerName', header: t('table.customerName') },
      { accessor: 'contractNumber', header: t('table.contractNumber') },
      { accessor: 'meteringPointsCount', header: t('table.meteringPointsCount') },
      {
        accessor: 'status',
        header: t('table.status'),
        value: ({ row }: { row: Contract }) => getStatusTranslation({ status: row.status, t }),
      },
      {
        accessor: 'createdDate',
        header: t('table.date'),
        value: ({ row }: { row: Contract }) => formatDate({ date: row.createdDate }),
      },
      { header: '', value: ActiveFromCell, virtualColumn: 'activeFrom' },
    ];

    return columns;
  }, [t]);

  return (
    <DataTable<Contract>
      columns={tableColumns}
      uid="contractNumber"
      data-testid="contract-table"
      data={list}
      renderCell={(row, accessor) => {
        const Cell = CELL_RENDER_MAP[accessor];

        if (!Cell) {
          return null;
        }

        return <Cell row={row} />;
      }}
    />
  );
};
